/**
 * Face Comparator Service
 *
 * Client-side face comparison to detect face swaps during verification.
 * Uses Procrustes-style landmark geometry comparison to check if the same
 * person is present at different stages of the verification flow.
 *
 * This is NOT a biometric face matching system - it's a simple geometric
 * comparison to catch obvious face swaps (e.g., someone holding up a photo,
 * or a different person completing parts of verification).
 *
 * Captures selfies at 3 stages:
 * - face_position: When face is properly positioned
 * - liveness: After liveness check completes
 * - age_estimation: After age estimation
 *
 * Threshold: 0.6 similarity (~85% accuracy for detecting obvious swaps)
 */

import type { Landmark } from './face-utils';
import { FEATURE_INDICES, distance2D } from './face-utils';

// ============================================================
// Types
// ============================================================

/**
 * Stage names where selfies are captured
 */
export type CaptureStage = 'face_position' | 'liveness' | 'age_estimation';

/**
 * Captured selfie with normalized landmarks
 */
export interface CapturedSelfie {
  /** Stage when captured */
  stage: CaptureStage;
  /** ISO timestamp */
  timestamp: string;
  /** Normalized landmarks (key facial points) */
  landmarks: NormalizedLandmarks;
  /** Image data URL (optional, for debugging) */
  imageDataUrl?: string;
}

/**
 * Normalized facial landmarks for comparison
 * All coordinates are normalized relative to the inter-ocular distance
 * and centered on the midpoint between the eyes.
 */
export interface NormalizedLandmarks {
  /** Left eye outer corner */
  leftEye: { x: number; y: number };
  /** Right eye outer corner */
  rightEye: { x: number; y: number };
  /** Nose tip */
  noseTip: { x: number; y: number };
  /** Left mouth corner */
  mouthLeft: { x: number; y: number };
  /** Right mouth corner */
  mouthRight: { x: number; y: number };
  /** Chin point */
  chin: { x: number; y: number };
}

/**
 * Result of comparing two selfies
 */
export interface ComparisonResult {
  /** Similarity score (0 to 1, higher = more similar) */
  similarity: number;
  /** Whether the faces match (similarity >= threshold) */
  matched: boolean;
  /** Individual feature distances for debugging */
  featureDistances?: {
    nose: number;
    mouthLeft: number;
    mouthRight: number;
    chin: number;
  };
}

/**
 * Final face comparison result across all stages
 */
export interface FaceComparisonResult {
  /** Whether all face comparisons passed */
  passed: boolean;
  /** Overall similarity score (average of all comparisons) */
  averageSimilarity: number;
  /** Individual comparison results */
  comparisons: {
    stages: [CaptureStage, CaptureStage];
    result: ComparisonResult;
  }[];
  /** Number of selfies captured */
  captureCount: number;
  /** Error message if comparison failed */
  error?: string;
}

// ============================================================
// Constants
// ============================================================

/**
 * Similarity threshold for face matching
 * 0.6 provides ~85% accuracy for detecting obvious face swaps
 * while allowing for natural variation in pose/expression
 */
const SIMILARITY_THRESHOLD = 0.6;

/**
 * Maximum allowed distance for a feature to be considered matching
 * (in normalized units where inter-ocular distance = 1)
 */
const MAX_FEATURE_DISTANCE = 0.4;

// ============================================================
// Face Comparator Class
// ============================================================

/**
 * Manages face captures and comparisons during verification
 */
export class FaceComparator {
  private captures: Map<CaptureStage, CapturedSelfie> = new Map();
  private similarityThreshold: number;

  constructor(threshold: number = SIMILARITY_THRESHOLD) {
    this.similarityThreshold = threshold;
  }

  /**
   * Capture a selfie at the given stage
   *
   * @param stage - The verification stage
   * @param landmarks - Raw MediaPipe landmarks (468 points)
   * @param imageDataUrl - Optional image data URL for debugging
   * @returns true if capture successful
   */
  capture(stage: CaptureStage, landmarks: Landmark[], imageDataUrl?: string): boolean {
    if (!landmarks || landmarks.length < 468) {
      console.warn(`[FaceComparator] Invalid landmarks for stage ${stage}`);
      return false;
    }

    try {
      const normalized = this.normalizeLandmarks(landmarks);

      this.captures.set(stage, {
        stage,
        timestamp: new Date().toISOString(),
        landmarks: normalized,
        imageDataUrl,
      });

      console.log(
        `[FaceComparator] Captured selfie at stage: ${stage}, ` +
          `total captures: ${this.captures.size}`
      );
      return true;
    } catch (error) {
      console.error(`[FaceComparator] Error capturing at ${stage}:`, error);
      return false;
    }
  }

  /**
   * Check if a stage has been captured
   */
  hasCaptured(stage: CaptureStage): boolean {
    return this.captures.has(stage);
  }

  /**
   * Get all captured stages
   */
  getCapturedStages(): CaptureStage[] {
    return Array.from(this.captures.keys());
  }

  /**
   * Get capture count
   */
  getCaptureCount(): number {
    return this.captures.size;
  }

  /**
   * Compare two specific stages
   */
  compareStages(stage1: CaptureStage, stage2: CaptureStage): ComparisonResult | null {
    const capture1 = this.captures.get(stage1);
    const capture2 = this.captures.get(stage2);

    if (!capture1 || !capture2) {
      console.warn(
        `[FaceComparator] Missing captures: ${stage1}=${!!capture1}, ${stage2}=${!!capture2}`
      );
      return null;
    }

    return this.compareLandmarks(capture1.landmarks, capture2.landmarks);
  }

  /**
   * Run final comparison across all captured selfies
   *
   * Compares face_position → liveness and liveness → age_estimation
   * to ensure the same person completed all stages.
   *
   * @returns FaceComparisonResult with pass/fail and details
   */
  runComparison(): FaceComparisonResult {
    const stages = this.getCapturedStages();

    if (stages.length < 2) {
      return {
        passed: false,
        averageSimilarity: 0,
        comparisons: [],
        captureCount: stages.length,
        error: `Insufficient captures: ${stages.length} (need at least 2)`,
      };
    }

    const comparisons: FaceComparisonResult['comparisons'] = [];
    let totalSimilarity = 0;
    let allPassed = true;

    // Compare consecutive pairs
    const orderedStages: CaptureStage[] = ['face_position', 'liveness', 'age_estimation'];
    const capturedOrdered = orderedStages.filter((s) => this.captures.has(s));

    for (let i = 0; i < capturedOrdered.length - 1; i++) {
      const stage1 = capturedOrdered[i];
      const stage2 = capturedOrdered[i + 1];
      const result = this.compareStages(stage1, stage2);

      if (result) {
        comparisons.push({
          stages: [stage1, stage2],
          result,
        });
        totalSimilarity += result.similarity;

        if (!result.matched) {
          allPassed = false;
          console.warn(
            `[FaceComparator] Face mismatch between ${stage1} and ${stage2}: ` +
              `similarity=${result.similarity.toFixed(3)}`
          );
        }
      }
    }

    const averageSimilarity = comparisons.length > 0 ? totalSimilarity / comparisons.length : 0;

    return {
      passed: allPassed && comparisons.length > 0,
      averageSimilarity,
      comparisons,
      captureCount: stages.length,
    };
  }

  /**
   * Clear all captures (call on verification restart)
   */
  reset(): void {
    this.captures.clear();
    console.log('[FaceComparator] Reset - all captures cleared');
  }

  // ============================================================
  // Private Methods
  // ============================================================

  /**
   * Normalize landmarks for comparison
   *
   * Uses Procrustes-style normalization:
   * 1. Center on midpoint between eyes
   * 2. Scale by inter-ocular distance
   * 3. Extract key feature points
   */
  private normalizeLandmarks(landmarks: Landmark[]): NormalizedLandmarks {
    // Get key landmarks
    const leftEye = landmarks[FEATURE_INDICES.leftEyeOuter];
    const rightEye = landmarks[FEATURE_INDICES.rightEyeOuter];
    const noseTip = landmarks[FEATURE_INDICES.noseTip];
    const mouthLeft = landmarks[FEATURE_INDICES.mouthLeft];
    const mouthRight = landmarks[FEATURE_INDICES.mouthRight];
    const chin = landmarks[FEATURE_INDICES.chin];

    // Calculate inter-ocular distance for scaling
    const iod = distance2D({ x: leftEye.x, y: leftEye.y }, { x: rightEye.x, y: rightEye.y });

    // Midpoint between eyes (center point)
    const centerX = (leftEye.x + rightEye.x) / 2;
    const centerY = (leftEye.y + rightEye.y) / 2;

    // Normalize function: center and scale by IOD
    const normalize = (point: Landmark): { x: number; y: number } => ({
      x: (point.x - centerX) / iod,
      y: (point.y - centerY) / iod,
    });

    return {
      leftEye: normalize(leftEye),
      rightEye: normalize(rightEye),
      noseTip: normalize(noseTip),
      mouthLeft: normalize(mouthLeft),
      mouthRight: normalize(mouthRight),
      chin: normalize(chin),
    };
  }

  /**
   * Compare two sets of normalized landmarks
   */
  private compareLandmarks(a: NormalizedLandmarks, b: NormalizedLandmarks): ComparisonResult {
    // Calculate distances for each feature (eyes are already normalized to same position)
    const distances = {
      nose: this.pointDistance(a.noseTip, b.noseTip),
      mouthLeft: this.pointDistance(a.mouthLeft, b.mouthLeft),
      mouthRight: this.pointDistance(a.mouthRight, b.mouthRight),
      chin: this.pointDistance(a.chin, b.chin),
    };

    // Average distance (lower = more similar)
    const avgDistance =
      (distances.nose + distances.mouthLeft + distances.mouthRight + distances.chin) / 4;

    // Convert to similarity score (0 to 1)
    // Using exponential decay for smoother scoring
    const similarity = Math.exp(-avgDistance / MAX_FEATURE_DISTANCE);

    return {
      similarity,
      matched: similarity >= this.similarityThreshold,
      featureDistances: distances,
    };
  }

  /**
   * Calculate Euclidean distance between two 2D points
   */
  private pointDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}

// ============================================================
// Singleton Instance
// ============================================================

let instance: FaceComparator | null = null;

/**
 * Get the singleton FaceComparator instance
 */
export function getFaceComparator(): FaceComparator {
  if (!instance) {
    instance = new FaceComparator();
  }
  return instance;
}

/**
 * Reset and get a new FaceComparator instance
 */
export function resetFaceComparator(): FaceComparator {
  instance = new FaceComparator();
  return instance;
}
